<template>
    <ConfirmDialog></ConfirmDialog>
    <section class="flex flex-col items-stretch mb-4 px-4 md:px-8">
        <div class="flex flex-wrap justify-center gap-4 w-full">
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="from-date">Desde</label>
                <Calendar id="from-date" v-model="fromDate" showIcon :showOnFocus="false" class="w-full" dateFormat="dd/mm/yy" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="to-date">Hasta</label>
                <Calendar id="to-date" v-model="toDate" showIcon :showOnFocus="false" class="w-full" dateFormat="dd/mm/yy" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="client">Cliente</label>
                <Dropdown id="client" v-model="selectedClient" :options="clients" optionLabel="name" placeholder="Selecciona un cliente"
                class="w-full" :disabled="formDisabled" filter/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="payment-status">Estado</label>
                <Dropdown id="payment-status" v-model="selectedPaymentStatus" :options="paymentStatus" optionLabel="name" 
            placeholder="Selecciona un estado de pago" class="w-full" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="company-name">Razón Social</label>
                <InputText id="company-name" v-model="companyName" placeholder="Razón Social" class="w-full" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="company">Empresa</label>
                <Dropdown id="company" v-model="selectedCompany" :options="companies" optionLabel="name" placeholder="Selecciona una empresa" class="w-full" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="serial">Folio</label>
                <InputText id="serial" v-model="serial" placeholder="Folio de la factura" class="w-full" :disabled="formDisabled"/>
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="opacity-0" for="search-btn">Acción</label>
                <Button id="search-btn" label="Buscar" @click="fillTable" :disabled="buttonDisabled"></Button>
            </div>
        </div>
    </section>
    <section class="grid justify-center mb-4 px-4 md:px-8" v-show="invoices.length > 0">
        <div class="mb-2 flex flex-col md:flex-row md:justify-between gap-2">
            <div class="flex gap-2">
                <Button 
                    @click="exportCSV" 
                    class="btn-csv export-btn" 
                    v-tooltip.top="'Exportar CSV'"
                >
                    <img src="../assets/svg/file_csv.svg" alt="CSV" class="w-5 h-5" />
                </Button>
                <Button 
                    @click="exportXLSX" 
                    class="btn-xlsx export-btn" 
                    v-tooltip.top="'Exportar XLSX'"
                >
                    <img src="../assets/svg/file_xlsx.svg" alt="XLSX" class="w-5 h-5" />
                </Button>
                <Button 
                    @click="exportPDF" 
                    class="btn-pdf export-btn" 
                    v-tooltip.top="'Exportar PDF'"
                >
                    <img src="../assets/svg/file_pdf.svg" alt="PDF" class="w-5 h-5" />
                </Button>
                <Button 
                    @click="printTable" 
                    class="btn-print export-btn" 
                    v-tooltip.top="'Imprimir'"
                >
                    <img src="../assets/svg/file_print.svg" alt="Imprimir" class="w-5 h-5" />
                </Button>
            </div>
            <InputText
                v-model="globalFilter"
                placeholder="Buscar en todos los campos"
                class="w-full md:w-80"
            />
        </div>
        <div class="card overflow-auto" v-if="filteredInvoices.length > 0">
            <DataTable
                :value="filteredInvoices"
                stripedRows
                tableStyle="min-width: 30rem"
                class="min-w-full view-invoices-table"
                @row-click="onRowClick">
                <Column field="serial_str" header="Folio">
                    <template #body="{data}">
                        <span class="font-semibold text-xl">{{ data.serial_str }}</span>
                    </template>
                </Column>
                <Column field="description" header="Descripción">
                    <template #body="{data}">
                        <div>{{ data.description }}</div>
                    </template>
                </Column>
                <Column field="name" header="Razón Social">
                    <template #body="{data}">
                        <span class="font-bold">{{ data.name }}</span>
                    </template>
                </Column>
                <Column field="date" header="Fecha">
                    <template #body="{data}">
                        <span>{{ data.date }}</span>
                    </template>
                </Column>
                <Column field="paid_label" header="Estado de pago">
                    <template #body="{data}">
                        <span class="font-bold">{{ data.paid_label }}</span>
                    </template>
                </Column>
                <Column field="total" header="Monto">
                    <template #body="{data}">
                        <span class="text-2xl">{{ data.total }}</span>
                    </template>
                </Column>
            </DataTable>
        </div>
        <div v-else class="text-center text-xl font-bold p-4">
            No se encontraron facturas que coincidan con los criterios de búsqueda.
        </div>
    </section>
    <Dialog v-model:visible="showInvoiceDetailsDialog" modal :header="`Factura ${invoiceDetails.serial_str}`" :style="{ width: '80rem' }" class="invoice-details-dialog">
        <div class="grid grid-cols-2 gap-4">
            <div>
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">Razón Social</span>
                    <span class="text-xl">{{ invoiceDetails.name }}</span>
                </div>
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">Nombre comercial</span>
                    <span class="text-xl">{{ invoiceDetails.client }}</span>
                </div>
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">Tipo</span>
                    <span class="text-xl">{{ invoiceDetails.type }}</span>
                </div>
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">Descripción</span>
                    <span class="text-xl">{{ invoiceDetails.description }}</span>
                </div>
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">Sucursal</span>
                    <span class="text-xl">{{ invoiceDetails.branch_name }}</span>
                </div>
            </div>
            <div>
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">Subtotal</span>
                    <span class="text-xl">{{ `$${invoiceDetails.subtotal}` }}</span>
                </div>
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">IVA</span>
                    <span class="text-xl">{{ `$${invoiceDetails.tax}` }}</span>
                </div>
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">Abonos</span>
                    <DataTable :value="invoiceDetails.payments" tableStyle="min-width: 10rem" class="min-w-full invoice-payments-table">
                        <Column field="description">
                            <template #body="{data}">
                                <span class="font-bold">{{ data.date }}</span>
                            </template>
                        </Column>
                        <Column field="bank" header="">
                            <template #body="{data}">
                                <div v-if="data.bank">
                                    <img v-if="data.bank?.image" :src="data.bank.image" :alt="`${data.bank?.name}`" class="inline-block w-8 h-8 mr-2 rounded" v-tooltip.top="data.bank?.name"/>
                                    <span v-else class="font-bold">{{ `${data.bank?.name}` }}</span>
                                </div>
                            </template>
                        </Column>
                        <Column field="amount">
                            <template #body="{data}">
                                <span class="font-bold">{{ `$${data.amount}` }}</span>
                            </template>
                        </Column>
                        <Column field="delete">
                            <template #body="{data}">
                                <Button icon="pi pi-trash" @click="killPayment(data)" rounded v-show="data.date != 'Total'" class="mini-btn" :disabled="invoiceDetailsButtonsDisabled || !user_permissions.includes('register_invoices')"/>
                            </template>
                        </Column>
                    </DataTable>
                </div>
                
                <div class="grid justify-items-center text-center mb-4">
                    <span class="font-bold text-lg">Agregar abono</span>

                    <div class="flex gap-2 items-end w-full max-w-3xl p-fluid">

                        <div class="grid flex-1 min-w-0">
                            <label for="fecha">Fecha</label>
                            <Calendar
                                inputId="fecha"
                                v-model="invoiceDate"
                                showIcon
                                :showOnFocus="false"
                                dateFormat="dd/mm/yy"
                                class="w-full"
                                inputClass="w-full"
                                :disabled="!user_permissions.includes('register_invoices')"
                            />
                        </div>

                        <div class="grid flex-1 min-w-0">
                            <label for="bank">Banco</label>
                            <Dropdown
                                inputId="bank"
                                v-model="selectedBank"
                                class="w-full"
                                inputClass="w-full"
                                :options="banks"
                                optionLabel="name"
                                placeholder="Selecciona un banco"
                                :disabled="!user_permissions.includes('register_expenses')"
                            >
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex align-items-center">
                                        <img :alt="slotProps.value.name" :src="slotProps.value.image" class="mr-2" style="width: 18px; height: 18px;" />
                                        <div>{{ slotProps.value.name }}</div>
                                    </div>
                                    <span v-else>
                                        {{ slotProps.placeholder }}
                                    </span>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex align-items-center">
                                        <img :alt="slotProps.option.name" :src="slotProps.option.image" class="mr-2" style="width: 18px; height: 18px;" />
                                        <div>{{ slotProps.option.name }}</div>
                                    </div>
                                </template>
                            </Dropdown>
                        </div>

                        <div class="grid flex-1 min-w-0">
                            <label for="cantidad">Cantidad</label>
                            <InputNumber
                                inputId="cantidad"
                                v-model="amount"
                                class="w-full"
                                inputClass="w-full"
                                :maxFractionDigits="2"
                                :disabled="!user_permissions.includes('register_invoices')"
                                @paste="handlePaste"
                            />
                        </div>

                        <div class="flex items-end flex-none">
                            <Button icon="pi pi-plus" rounded class="mini-btn" @click="addPayment()" :disabled="invoiceDetailsButtonsDisabled || !user_permissions.includes('register_invoices')"/>
                        </div>
                    </div>
                </div>

                <div class="flex justify-center mt-4 gap-4">
                    <Button @click="killInvoice()" :disabled="invoiceDetailsButtonsDisabled || !user_permissions.includes('register_invoices')">Eliminar</Button>
                </div>
            </div>
        </div>
    </Dialog>
</template>

<script>
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axiosInstance from '../axiosInstance';
    export default {
        data() {
            return {
                fromDate: new Date(),
                toDate: new Date(),
                clients: [],
                selectedClient: {},
                paymentStatus: [],
                selectedPaymentStatus: {},
                companyName: "",
                companies: [],
                selectedCompany: {},
                serial: "",
                invoices: [],
                invoiceDetails: {},
                invoiceDate: new Date(),
                amount: null,
                showInvoiceDetailsDialog: false,
                formDisabled: false,
                buttonDisabled: false,
                invoiceDetailsButtonsDisabled: false,
                globalFilter: "",
                exportFields: {
                    serial_str: "Folio",
                    description: "Descripción",
                    name: "Razón Social",
                    invoice_period: "Fecha",
                    paid_label: "Estado de pago",
                    total: "Monto"
                },
                user_permissions: [],
                dateRange: "",
                banks: [],
                selectedBank: {}
            }
        },
        methods: {
            async fetchInitialData() {
                this.$emit("update-loading", true);
                this.formDisabled = true;
                this.buttonDisabled = true;
                let data = {};
                try {
                    const response = axiosInstance.post("/api/invoices", {
                        token: localStorage.getItem('token')
                    });

                    data = await response;
            
                } catch(error) {
                    console.error("fetchInitialData: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                }

                return data;
            },

            setInitialData() {
                this.fetchInitialData().then((response) => {
                    if(response.status === 200) {
                        const { from: fromDate, to: toDate, defaultCmp, companies, clients, banks } = response.data;
                        
                        this.selectedCompany = {
                            name: defaultCmp.data.name, 
                            id: defaultCmp.data.id, 
                            branches: defaultCmp.branches
                        }

                        this.companies = companies.map((company) => {
                            return {
                                name: company.data.name,
                                id: company.data.id,
                                branches: company.branches
                            }
                        });

                        
                        this.clients = clients;
                        this.clients.unshift({ name: "Todos", id: 0 });
                        this.selectedClient = this.clients[0];

                        this.fromDate = this.obtainDateType(fromDate);
                        this.toDate = this.obtainDateType(toDate);

                        this.banks = banks;

                        this.formDisabled = false;
                        this.buttonDisabled = false;
                    } else {
                        console.warn("setInitialData: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            async fetchInvoicesSearch() {
                this.$emit("update-loading", true);
                this.buttonDisabled = true;
                let data = {};
                try {
                    const response = axiosInstance.post("/api/invoices_search", {
                        token: localStorage.getItem('token'),
                        from: this.formatDate(this.fromDate),
                        to: this.formatDate(this.toDate),
                        name: this.companyName,
                        client: this.selectedClient.id,
                        company_id: this.selectedCompany.id,
                        status: this.selectedPaymentStatus.id,
                        serial_str: this.serial.trim()
                    });

                    data = await response;
            
                } catch(error) {
                    console.error("fetchInvoicesSearch: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                    this.buttonDisabled = false;
                }

                return data;
            },

            fillTable(){
                this.fetchInvoicesSearch().then((response) => {
                    if(response.status == 200) {
                        this.dateRange = `${this.formatDate(this.fromDate).replace(/-/g, '')}-${this.formatDate(this.toDate).replace(/-/g, '')}`;
                        const { items } = response.data;
                        this.invoices = items;
                    } else {
                        console.warn("fillTable: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            async fetchInvoiceDetails(invoiceId) {
                this.$emit("update-loading", true);
                let data = {};
                try {
                    const response = axiosInstance.post("/api/invoice_show", {
                        token: localStorage.getItem('token'),
                        id: invoiceId
                    });

                    data = await response;
            
                } catch(error) {
                    console.error("fetchInvoiceDetails: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                }

                return data;
            },

            onRowClick(event) {
                const invoiceId = event.data.id;
                this.fetchInvoiceDetails(invoiceId).then((response) => {
                    if(response.status === 200) {
                        const invoiceDetails = response.data;
                        this.invoiceDetails = invoiceDetails;
                        this.invoiceDate = this.obtainDateType(invoiceDetails.default_date);
                        this.selectedBank = this.banks[0];
                        this.invoiceDetails.payments.push({
                            date: "Total",
                            amount: invoiceDetails.total
                        })
                        this.amount = null;
                        this.showInvoiceDetailsDialog = true;
                    } else {
                        console.warn("onRowClick: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                });
            },

            async pushPayment(){
                this.$emit("update-loading", true);
                let data = {};
                this.invoiceDetailsButtonsDisabled = true;
                try {
                    const request = {
                        token: localStorage.getItem('token'),
                        paid_at: this.formatDate(this.invoiceDate),
                        expense_id: this.invoiceDetails.id,
                        bank_id: this.selectedBank.id,
                        id: this.invoiceDetails.id
                    };

                    if(this.amount != null || this.amount > 0) {
                        request.amount = this.amount;
                    }

                    const response = axiosInstance.post("/api/invoice_payment", request);

                    data = await response;
            
                } catch(error) {
                    console.error("pushPayment: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                    this.invoiceDetailsButtonsDisabled = false;
                }

                return data;
            },

            addPayment(){
                this.pushPayment().then((response) => {
                    if(response.status === 200) {
                        if(response.data.error && response.data.message){
                            const { message } = response.data;
                            this.$toast.add({ severity: 'warn', summary: 'Aviso',
                            detail: message, life: 5000, group: 'topr' });
                        } else {
                            this.$toast.add({ severity: 'success', summary: 'Éxito',
                            detail: 'El pago se agregó correctamente.', life: 5000, group: 'topr' });
                            this.showInvoiceDetailsDialog = false;
                            this.fillTable();
                        }
                    } else {
                        console.warn("addPayment: There was a problem with the request.", response);
                        this.$toast.add({ severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                    }
                })
            },

            async deleteInvoice(){
                this.$emit("update-loading", true);
                let data = {};
                this.invoiceDetailsButtonsDisabled = true;
                try {
                    const request = {
                        token: localStorage.getItem('token'),
                        id: this.invoiceDetails.id
                    };
                    const response = axiosInstance.post("/api/invoice_kill", request);

                    data = await response;
            
                } catch(error) {
                    console.error("deleteInvoice: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                    this.invoiceDetailsButtonsDisabled = false;
                }

                return data;
            },

            killInvoice() {
                this.$confirm.require({
                    header: 'Confirmación de eliminación',
                    message: `¿Deseas eliminar la factura ${this.invoiceDetails.serial_str}?`,
                    icon: 'pi pi-exclamation-triangle',
                    rejectLabel: 'Cancelar',
                    acceptLabel: 'Aceptar',
                    rejectClass: 'p-button-secondary p-button-outlined',
                    acceptClass: 'p-button-danger',
                    accept: () => {
                        this.deleteInvoice().then((response) => {
                            if(response.status === 200) {
                                if(response.data.error && response.data.message){
                                    const { message } = response.data;
                                    this.$toast.add({ severity: 'warn', summary: 'Aviso',
                                    detail: message, life: 5000, group: 'topr' });
                                } else {
                                    this.$toast.add({ severity: 'success', summary: 'Éxito',
                                    detail: 'La factura se eliminó correctamente.', life: 5000, group: 'topr' });
                                    this.showInvoiceDetailsDialog = false;
                                    this.fillTable();
                                }
                            } else {
                                console.warn("killInvoice: There was a problem with the request.", response);
                                this.$toast.add({ severity: 'warn', summary: 'Aviso',
                                detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                            }
                        })
                    }
                });
            },

            async deletePayment(id) {
                this.$emit("update-loading", true);
                let data = {};
                this.invoiceDetailsButtonsDisabled = true;
                try {
                    const response = axiosInstance.post("/api/invoice_payment_kill", {
                        token: localStorage.getItem('token'),
                        id: id
                    });

                    data = await response;
            
                } catch(error) {
                    console.error("deletePayment: There was an error!", error);
                    this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos.', life: 5000, group: 'topr' });
                } finally {
                    this.$emit("update-loading", false);
                    this.invoiceDetailsButtonsDisabled = false;
                }

                return data;
            },

            killPayment(data) {
                this.$confirm.require({
                    header: 'Confirmación de eliminación',
                    message: `¿Deseas eliminar el pago de $${data.amount}?`,
                    icon: 'pi pi-exclamation-triangle',
                    rejectLabel: 'Cancelar',
                    acceptLabel: 'Aceptar',
                    rejectClass: 'p-button-secondary p-button-outlined',
                    acceptClass: 'p-button-danger',
                    accept: () => {
                        this.deletePayment(data.id).then((response) => {
                            if(response.status === 200) {
                                if(response.data.error && response.data.message){
                                    const { message } = response.data;
                                    this.$toast.add({ severity: 'warn', summary: 'Aviso',
                                    detail: message, life: 5000, group: 'topr' });
                                } else {
                                    this.$toast.add({ severity: 'success', summary: 'Éxito',
                                    detail: 'El pago se eliminó correctamente.', life: 5000, group: 'topr' });
                                    this.showInvoiceDetailsDialog = false;
                                    this.fillTable();
                                }
                            } else {
                                console.warn("killPayment: There was a problem with the request.", response);
                                this.$toast.add({ severity: 'warn', summary: 'Aviso',
                                detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', life: 5000, group: 'topr' });
                            }
                        })
                    }
                });
            },

            exportCSV() {
                if (!this.filteredInvoices.length) return;
                const items = this.filteredInvoices;
                const fields = Object.keys(this.exportFields);
                const header = Object.values(this.exportFields);

                const replacer = (key, value) => value === null ? '' : value;
                const csv = [
                    header.join(','),
                    ...items.map(row => fields.map(fieldName =>
                        JSON.stringify(row[fieldName], replacer)).join(','))
                ].join('\r\n');

                const BOM = '\uFEFF';
                const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `facturas_${this.dateRange}.csv`;
                link.click();
                URL.revokeObjectURL(link.href);
            },

            exportXLSX() {
                if (!this.filteredInvoices.length) return;
                const fields = Object.keys(this.exportFields);
                const data = this.filteredInvoices.map(item => {
                    const row = {};
                    fields.forEach(key => {
                        row[this.exportFields[key]] = item[key];
                    });
                    return row;
                });
                const ws = XLSX.utils.json_to_sheet(data);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Facturas");
                XLSX.writeFile(wb, `facturas_${this.dateRange}.xlsx`);
            },

            exportPDF() {
                if (!this.filteredInvoices.length) return;
                const doc = new jsPDF();
                const fields = Object.keys(this.exportFields);
                const headers = [Object.values(this.exportFields)];
                const data = this.filteredInvoices.map(item =>
                    fields.map(key => item[key])
                );

                autoTable(doc, {
                    head: headers,
                    body: data,
                    styles: { font: "helvetica", fontSize: 10 },
                    headStyles: { fillColor: [31, 41, 55] },
                    margin: { top: 20 }
                });

                doc.save(`facturas_${this.dateRange}.pdf`);
            },

            printTable() {
                if (!this.filteredInvoices.length) return;
                const fields = Object.keys(this.exportFields);
                const headers = Object.values(this.exportFields);

                const table = document.createElement('table');
                table.style.borderCollapse = 'collapse';
                table.style.width = '100%';
                table.style.fontFamily = 'sans-serif';

                const thead = document.createElement('thead');
                const trHead = document.createElement('tr');
                headers.forEach(h => {
                    const th = document.createElement('th');
                    th.textContent = h;
                    th.style.background = '#f3f4f6';
                    th.style.padding = '8px';
                    th.style.border = '1px solid #ccc';
                    trHead.appendChild(th);
                });
                thead.appendChild(trHead);
                table.appendChild(thead);

                const tbody = document.createElement('tbody');
                this.filteredInvoices.forEach(row => {
                    const tr = document.createElement('tr');
                    fields.forEach(f => {
                        const td = document.createElement('td');
                        td.textContent = row[f] ?? "";
                        td.style.padding = '8px';
                        td.style.border = '1px solid #ccc';
                        tr.appendChild(td);
                    });
                    tbody.appendChild(tr);
                });
                table.appendChild(tbody);

                const printWindow = window.open('', '', 'width=900,height=700');
                if (!printWindow) return;

                const doc = printWindow.document;
                doc.title = "Imprimir facturas";

                const h2 = doc.createElement('h2');
                h2.textContent = "Facturas";
                doc.body.appendChild(h2);

                doc.body.appendChild(table);

                setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                }, 300);
            },

            obtainDateType(date){
                const [dateY, dateM, dateD] = date.split("-");
                return new Date(Number(dateY), Number(dateM) - 1, Number(dateD))
            },

            formatDate(date){
                const month = ((date.getMonth()+1).toString()).padStart(2, "0");
                const day = ((date.getDate()).toString()).padStart(2, "0");
                return `${date.getFullYear()}-${month}-${day}`
            },
            handlePaste(event) {
                event.preventDefault();
                
                const pastedText = (event.clipboardData || window.clipboardData).getData('text');

                let cleanedText = pastedText.replace(/[$,]/g, '').trim();
                
                const numericValue = parseFloat(cleanedText);
                
                if (!isNaN(numericValue)) {
                    this.amount = numericValue;
                }
            },
        },

        mounted() {
            try {
                if (localStorage.getItem('clientData') !== null) {
                    const clientData = JSON.parse(localStorage.getItem('clientData'));
                    this.user_permissions = clientData.permissions;
                }
            } catch (error) {
                console.log("Error al obtener el usuario: "+error);
            }

            this.setInitialData();
            this.paymentStatus = [
                { name: "Todos", id: -1 },
                { name: "Sin pagar", id: 0 },
                { name: "Pagado", id: 1 }
            ];
            this.selectedPaymentStatus = this.paymentStatus[0];

            // Observar tooltips para aplicar estilos personalizados
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1 && node.classList && node.classList.contains('p-tooltip')) {
                            const tooltipText = node.querySelector('.p-tooltip-text');
                            const tooltipArrow = node.querySelector('.p-tooltip-arrow');
                            
                            if (tooltipText) {
                                const text = tooltipText.textContent;
                                let color = null;
                                
                                if (text === 'Exportar CSV') color = '#3b82f6';
                                else if (text === 'Exportar XLSX') color = '#16a34a';
                                else if (text === 'Exportar PDF') color = '#dc2626';
                                else if (text === 'Imprimir') color = '#eab308';
                                
                                if (color) {
                                    tooltipText.style.backgroundColor = color;
                                    tooltipText.style.color = 'white';
                                    if (tooltipArrow) {
                                        tooltipArrow.style.borderTopColor = color;
                                    }
                                }
                            }
                        }
                    });
                });
            });
            
            observer.observe(document.body, { childList: true, subtree: true });

            // Guardar el observer para limpiarlo después
            this._tooltipObserver = observer;
        },

        beforeUnmount() {
            if (this._tooltipObserver) {
                this._tooltipObserver.disconnect();
            }
        },

        computed: {
            filteredInvoices() {
                if (!this.globalFilter) return this.invoices;
                const filter = this.globalFilter.toLowerCase();
                return this.invoices.filter(inv =>
                    Object.values(inv).some(val =>
                        String(val).toLowerCase().includes(filter)
                    )
                );
            }
        }
    }
</script>

<style scoped>
    ::v-deep(.invoice-payments-table .p-datatable-thead) {
        display: none;
    }

    ::v-deep(.view-invoices-table.p-datatable) {
        border-radius: 0.5rem !important;
        overflow: hidden !important;
        box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) !important;
    }

    ::v-deep(.view-invoices-table.p-datatable .p-datatable-wrapper) {
        border-radius: 0.5rem !important;
        overflow: hidden !important;
    }

    ::v-deep(.view-invoices-table.p-datatable .p-datatable-thead > tr > th) {
        background-color: #0f1338 !important;
        color: white !important;
        font-size: 1rem !important;
        font-weight: 600 !important;
        padding: 0.75rem 1rem !important;
        border-bottom: 2px solid #374151 !important;
    }

    ::v-deep(.view-invoices-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td) {
        background-color: #f3f4f6 !important;
        color: #111827 !important;
    }

    ::v-deep(.view-invoices-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td) {
        background-color: #ffffff !important;
        color: #111827 !important;
    }

    ::v-deep(.view-invoices-table.p-datatable .p-datatable-tbody > tr > td) {
        padding: 0.5rem 1rem !important;
        border-bottom: 1px solid #e5e7eb !important;
        vertical-align: middle !important;
    }

    ::v-deep(.view-invoices-table.p-datatable .p-datatable-tbody > tr) {
        cursor: pointer !important;
        transition: background-color 0.3s ease !important;
    }

    ::v-deep(.view-invoices-table.p-datatable .p-datatable-tbody > tr:hover > td) {
        background-color: #fef3c7 !important;
        color: #111827 !important;
    }

    Button:not(.btn-csv):not(.btn-xlsx):not(.btn-pdf):not(.btn-print):not(.export-btn) {
        @apply bg-zinc-200 text-zinc-900 border border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 rounded px-4 py-2 text-lg shadow focus:outline-none transition-colors;
    }

    /* Export buttons base styles */
    ::v-deep(.export-btn) {
        @apply rounded p-2 shadow transition-colors flex items-center justify-center;
        width: 2.5rem;
        height: 2.5rem;
    }

    ::v-deep(.export-btn img) {
        filter: brightness(0) invert(1);
    }

    /* Export buttons styles: mantienen color original */
    ::v-deep(.btn-csv) {
        @apply bg-blue-500 text-white dark:bg-blue-700 dark:text-white;
        border: 1px solid #2563eb;
    }
    ::v-deep(.btn-csv:hover) {
        @apply bg-blue-600 text-white dark:bg-blue-800 dark:text-white !important;
        border: 1px solid #1d4ed8 !important;
    }

    ::v-deep(.btn-xlsx) {
        @apply bg-green-600 text-white dark:bg-green-800 dark:text-white;
        border: 1px solid #16a34a;
    }
    ::v-deep(.btn-xlsx:hover) {
        @apply bg-green-700 text-white dark:bg-green-900 dark:text-white !important;
        border: 1px solid #166534 !important;
    }

    ::v-deep(.btn-pdf) {
        @apply bg-red-600 text-white dark:bg-red-800 dark:text-white;
        border: 1px solid #dc2626;
    }
    ::v-deep(.btn-pdf:hover) {
        @apply bg-red-700 text-white dark:bg-red-900 dark:text-white !important;
        border: 1px solid #991b1b !important;
    }

    ::v-deep(.btn-print) {
        @apply bg-yellow-500 text-white dark:bg-yellow-700 dark:text-white !important;
        border: 1px solid #eab308;
    }
    ::v-deep(.btn-print:hover) {
        @apply bg-yellow-600 text-white dark:bg-yellow-800 dark:text-white !important;
        border: 1px solid #ca8a04 !important;
    }

    /* Calendar styles: neutro en ambos modos */
    ::v-deep(.p-calendar .p-button) {
        @apply bg-zinc-200 text-zinc-900 border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 transition-colors;
    }
    ::v-deep(.p-calendar .p-button:hover) {
        @apply bg-zinc-300 dark:bg-zinc-600;
    }

    /* Otros botones pequeños (mini-btn) */
    ::v-deep(.mini-btn) {
        @apply bg-zinc-200 text-zinc-900 border border-zinc-300 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:border-zinc-600 dark:hover:bg-zinc-600 transition-colors !important;
    }
    ::v-deep(.mini-btn:hover) {
        @apply bg-zinc-300 dark:bg-zinc-600 !important;
    }
</style>

<style>
.dark .view-invoices-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .view-invoices-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .view-invoices-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .view-invoices-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .view-invoices-table.p-datatable .p-datatable-tbody > tr:hover > td {
    background-color: #422006 !important;
    color: white !important;
}

/* Estilos globales para tooltips de botones de exportación */
[data-tooltip-color="blue"] + .p-tooltip .p-tooltip-text,
.p-tooltip[aria-describedby*="blue"] .p-tooltip-text {
    background-color: #3b82f6 !important;
}
[data-tooltip-color="blue"] + .p-tooltip .p-tooltip-arrow {
    border-top-color: #3b82f6 !important;
}

[data-tooltip-color="green"] + .p-tooltip .p-tooltip-text {
    background-color: #16a34a !important;
}
[data-tooltip-color="green"] + .p-tooltip .p-tooltip-arrow {
    border-top-color: #16a34a !important;
}

[data-tooltip-color="red"] + .p-tooltip .p-tooltip-text {
    background-color: #dc2626 !important;
}
[data-tooltip-color="red"] + .p-tooltip .p-tooltip-arrow {
    border-top-color: #dc2626 !important;
}

[data-tooltip-color="yellow"] + .p-tooltip .p-tooltip-text {
    background-color: #eab308 !important;
}
[data-tooltip-color="yellow"] + .p-tooltip .p-tooltip-arrow {
    border-top-color: #eab308 !important;
}

/* Delete button in dialog - light mode (ver-facturas only) */
html:not(.dark) .invoice-details-dialog .p-dialog-content .p-button:not(.mini-btn) {
    background-color: #0f1338 !important;
    border-color: #0f1338 !important;
    color: white !important;
}

html:not(.dark) .invoice-details-dialog .p-dialog-content .p-button:not(.mini-btn):hover:not(:disabled) {
    background-color: #1a1f4d !important;
    border-color: #1a1f4d !important;
}

/* Delete button in dialog - dark mode (ver-facturas only) */
html.dark .invoice-details-dialog .p-dialog-content .p-button:not(.mini-btn) {
    background-color: #D9D9D9 !important;
    border-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

html.dark .invoice-details-dialog .p-dialog-content .p-button:not(.mini-btn):hover:not(:disabled) {
    background-color: #c4c4c4 !important;
    border-color: #c4c4c4 !important;
}
</style>